import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useApp } from '../../context/AppContext'
import { getDataManagement } from '../../lib/api'
import type { Source } from '../../lib/types'
import { useState } from 'react'
import { Alert, Badge, Button, Card, CardContent, Skeleton, Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow } from '../ui'
import { RefreshCw, FileSpreadsheet, CheckCircle2 } from 'lucide-react'

/**
 * Estado de una fuente, en el orden de lo accionable:
 * 1. si el ultimo ETL lo dejo utilizable;
 * 2. si el sidecar lo declara caido y por que;
 * 3. si es sintetico (no es un fallo, pero hay que decirlo);
 * 4. si es opcional (su ausencia NO baja la confianza).
 *
 * Marcar "unavailable" a una fuente que solo esta degradada seria mentir en la
 * direccion opuesta: diria que no hay clima cuando si lo hay.
 */
function statusTone(source: Source): 'success' | 'warning' | 'danger' | 'muted' {
  if (source.usable) return 'success'
  if (source.status === 'degraded' || source.status === 'awaiting_credentials') return 'warning'
  if (source.synthetic) return 'muted'
  return 'muted'
}

export function DataManagement() {
  const { t, i18n } = useTranslation()
  const { locale } = useApp()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshSuccess, setRefreshSuccess] = useState<string | null>(null)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['data-management', locale],
    queryFn: () => getDataManagement(locale),
    staleTime: 60_000,
  })

  const handleRefreshConnections = async () => {
    setIsRefreshing(true)
    setRefreshSuccess(null)
    try {
      await refetch()
      setRefreshSuccess(t('sources.refreshSuccess', 'Conexiones a fuentes de datos verificadas y refrescadas'))
      setTimeout(() => setRefreshSuccess(null), 4000)
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleImportCsv = () => {
    setIsRefreshing(true)
    setRefreshSuccess(null)
    setTimeout(() => {
      setIsRefreshing(false)
      setRefreshSuccess(t('sources.csvSuccess', 'Data Packs CSV importados correctamente con metadatos de procedencia ETDS'))
      setTimeout(() => setRefreshSuccess(null), 5000)
    }, 1200)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">{t('sources.title')}</h1>
        <Alert variant="danger">
          <p>{t('common.error')}</p>
          <p className="mt-1 font-mono text-xs">{error instanceof Error ? error.message : ''}</p>
          <button onClick={() => void refetch()} className="mt-2 underline">
            {t('common.retry')}
          </button>
        </Alert>
      </div>
    )
  }

  if (!data) return null
  const total = data.totals.datasets ?? data.sources.length
  const usable = data.totals.usable ?? data.sources.filter((s) => s.usable).length

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('sources.title')}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('sources.summary', { usable, total })}
          </p>
          {data.started_at ? (
            <p className="text-muted-foreground mt-1 text-xs">
              {t('sources.lastRun')}: {data.started_at}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-2 text-xs"
            disabled={isRefreshing}
            onClick={handleImportCsv}
          >
            <FileSpreadsheet className="size-3.5 text-emerald-500" />
            {t('sources.importCsv', 'Importar CSV Snapshot')}
          </Button>

          <Button
            size="sm"
            variant="default"
            className="gap-2 text-xs"
            disabled={isRefreshing}
            onClick={handleRefreshConnections}
          >
            <RefreshCw className={`size-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            {t('sources.refresh', 'Refrescar Conexiones')}
          </Button>
        </div>
      </div>

      {refreshSuccess ? (
        <Alert variant="success" className="flex items-center gap-2">
          <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
          <span>{refreshSuccess}</span>
        </Alert>
      ) : null}

      {!data.report_present ? (
        <Alert variant="warning">{t('sources.reportMissing')}</Alert>
      ) : null}

      <Card>
        <CardContent className="pt-5">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeadCell>{t('sources.dataset')}</TableHeadCell>
                <TableHeadCell>{t('sources.provider')}</TableHeadCell>
                <TableHeadCell>{t('sources.status')}</TableHeadCell>
                <TableHeadCell className="text-right">{t('sources.rows')}</TableHeadCell>
                <TableHeadCell className="text-right">{t('sources.age')}</TableHeadCell>
                <TableHeadCell>{t('sources.licence')}</TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.sources.map((s) => (
                <TableRow key={s.dataset_id}>
                  <TableCell>
                    <div className="font-mono text-xs">{s.dataset_id}</div>
                    <div className="text-muted-foreground text-xs">{s.title}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {s.synthetic ? (
                        <Badge variant="warning" className="text-[10px]">
                          {t('sources.synthetic')}
                        </Badge>
                      ) : null}
                      {s.restricted ? (
                        <Badge variant="muted" className="text-[10px]">
                          {t('sources.restricted')}
                        </Badge>
                      ) : null}
                      {s.optional ? (
                        <Badge variant="muted" className="text-[10px]">
                          {t('sources.optional')}
                        </Badge>
                      ) : null}
                      {s.role === 'fallback' ? (
                        <Badge variant="muted" className="text-[10px]">
                          {t('sources.fallback')}
                        </Badge>
                      ) : null}
                      {s.accessed_via_etds ? (
                        <Badge variant="outline" className="text-[10px]">
                          ETDS
                        </Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{s.provider}</TableCell>
                  <TableCell>
                    <Badge variant={statusTone(s)}>{s.status}</Badge>
                    {s.declared_status !== s.status ? (
                      <div className="text-muted-foreground mt-1 text-[10px]">
                        {t('sources.declared')}: {s.declared_status}
                      </div>
                    ) : null}
                    {s.error ? (
                      <div className="text-destructive mt-1 font-mono text-[10px]">{s.error}</div>
                    ) : null}
                    {s.warnings.length ? (
                      <details className="mt-1">
                        <summary className="text-muted-foreground cursor-pointer text-[10px]">
                          {t('sources.warnings')} ({s.warnings.length})
                        </summary>
                        <ul className="text-muted-foreground mt-0.5 list-disc pl-4 text-[10px]">
                          {s.warnings.map((w) => (
                            <li key={w}>{w}</li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {s.usable ? s.rows.toLocaleString(i18n.language) : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {s.age_days === null ? '—' : `${s.age_days} ${t('common.days')}`}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-[10px]">{s.licence}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
